import { assert, DefaultMap, definedMap, panic } from "@glideapps/ts-necessities";
import type { GroundValue, Row } from "../common/computation-model/data";
import { follow } from "../common/computation-model/data";
import type { RelativePath, RootPath } from "../common/computation-model/paths";
import { isKeyPath, isTopLevelPath } from "../common/computation-model/paths";
import type { TableName } from "../common/database-strings";
import type { InputOutputTables, TableGlideType } from "../common/description";
import {
    areTableNamesEqual,
    getTableColumn,
    getTableName,
    isPrimitiveType,
    makeInputOutputTables,
} from "../common/description";
import { makeRowID } from "../common/make-row-id";
import type {
    InternalRowContext,
    WireActionRunner,
    WireAlwaysEditableValue,
    WireComponentHydrationResult,
    WireComponentHydratorWithID,
    WireComponentWithFlags,
    WireHydratedSubComponents,
    WireHydrationBackend,
    WireRowActionHydrationValueProvider,
    WireRowComponentHydrationBackend,
    WireRowHydrationValueProvider,
    WireRowValueProviderMaker,
    WireTableComponentHydrationBackend,
    WireTableTransformValueProvider,
} from "../generator/components/wire";
import type { PageScreenPosition, ParsedPath, ParsedScreen } from "../wire/parsed-path";
import type { WireComponent } from "../wire/types";
import {
    addSubscriptionHandler,
    emptyInternalScreenContext,
    makeInternalScreenContext,
    makeTableSubscriptionSources,
} from "./internal";
import type {
    ActionHydrationContext,
    ComponentState,
    InternalTab,
    MutableTableSubscriptionSources,
    ScreenHydrationContext,
    StateValuesMap,
    SubscriptionHandlerAndPath,
    SubscriptionInfo,
    ValueProviderActionContext,
    ValueProviderContext,
} from "./internal-types";
import { getParsedScreens } from "./parsed-path";
import { RowSubscriptionHandler, TableSubscriptionHandler } from "./subscription";

export type InternalTabHydrator = (hb: RowHydrationValueProvider<ValueProviderActionContext>) => InternalTab;

// TODO: State management as we do it right now is a bit of a mess.  Each
// component has its own state manager, and for list components we use name
// and key prefixes to disambiguate between rows.
//
// A proper way to manage state would be to have a single state tree, where
// each component, or sub-row, or whatever, can claim a sub-tree.  We can load
// individual keys within that tree on demand from local storage.  I'm not
// sure how to do the "dirtying", but it might be as simple as having a bit on
// each sub-tree that replaces the `stateChanged` bit that we currently have
// on the `ComponentState`.
export class StateManager {
    public readonly stateValues: StateValuesMap = new Map();

    constructor(public readonly oldStateValues: StateValuesMap | undefined) {}

    public getState<T>(
        name: string,
        token: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        shouldSave: boolean = true,
        convert?: (v: unknown) => T
    ): WireAlwaysEditableValue<T> {
        if (convert === undefined) {
            convert = x => x as T;
        }

        const newExisting = this.stateValues.get(name);
        if (newExisting !== undefined) {
            const [v, t] = newExisting;
            assert(validate(v));
            return { value: v, onChangeToken: t };
        }

        let value: T;
        // It's important that we only take the value of the old state, and
        // not its token for reuse.  State loading gives us a dummy token, and
        // using that would cause weird effects.
        const existing = this.oldStateValues?.get(name)?.[0];
        if (existing !== undefined && validate(existing)) {
            value = existing;
        } else {
            value = defaultValue;
        }
        this.stateValues.set(name, [value, token, shouldSave, convert]);
        return { value, onChangeToken: token };
    }
}

abstract class HydrationValueProvider<TContext extends ValueProviderContext> implements WireRowHydrationValueProvider {
    protected readonly stateManager: StateManager;

    constructor(
        public readonly context: TContext,
        public readonly rowContext: InternalRowContext | undefined,
        oldStateValues: StateValuesMap | undefined,
        protected readonly stateSaveKey: string | undefined,
        protected readonly componentID: string | undefined
    ) {
        this.stateManager = new StateManager(oldStateValues);
    }

    protected abstract addGlobalKey(rootKey: string): void;
    protected abstract addColumnInRow(rootKey: string, rowID: string, columnKey: string): void;

    public getState<T>(
        name: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        shouldSave: boolean = true,
        convert?: (v: unknown) => T
    ): WireAlwaysEditableValue<T> {
        return this.stateManager.getState(
            name,
            `${this.componentID}-${this.rowContext?.inputRow?.$rowID}-${name}`,
            validate,
            defaultValue,
            shouldSave,
            convert
        );
    }

    public getGlobalValue(tableName: TableName | undefined, path: RootPath): GroundValue {
        this.addGlobalKey(path.rest.key);
        if (tableName !== undefined) {
            this.context.fetchTableData(tableName);
        }

        return this.context.namespace?.get(path);
    }

    public getColumnInRow(tableName: TableName, rootPath: RootPath, row: Row, columnPath: RelativePath): GroundValue {
        assert(isTopLevelPath(rootPath));
        assert(isKeyPath(columnPath) && columnPath.rest === undefined);

        this.addColumnInRow(rootPath.rest.key, row.$rowID, columnPath.key);
        this.context.fetchTableData(tableName);

        // If we didn't do this, the values in `row` would be out of data.
        // That's not usually a problem because we'd be re-invoked if we
        // subscribe, but when we run an action we might not a "loading"
        // value, which would cause us to run the action even though we should
        // wait.
        this.context.namespace?.get(rootPath);

        return follow(row, columnPath);
    }
}

abstract class HydrationBackendBase<TContext extends ValueProviderActionContext>
    extends HydrationValueProvider<TContext>
    implements WireHydrationBackend
{
    // TODO: Maybe this doesn't belong in here and should be abstracted.
    protected readonly tokens = new Set<string>();
    protected needsParsedPath = false;

    protected addToken(token: string): void {
        this.tokens.add(token);
    }

    public registerAction(run: WireActionRunner): string {
        const token = makeRowID();
        assert(this.rowContext !== undefined);
        this.context.actions.set(token, [run, this.rowContext]);
        this.addToken(token);
        return token;
    }

    public getParsedPath(): ParsedPath {
        this.needsParsedPath = true;
        return this.context.parsedPath;
    }
}

export class RowHydrationValueProvider<
    TContext extends ValueProviderActionContext
> extends HydrationBackendBase<TContext> {
    private readonly sources = {
        globalKeys: new Set<string>(),
        columnsInRows: new DefaultMap<string, DefaultMap<string, Set<string>>>(() => new DefaultMap(() => new Set())),
    };
    private subsidiaries: RowHydrationValueProvider<TContext>[] = [];
    private additionalSubscriptionInfo: SubscriptionInfo[] = [];

    protected addGlobalKey(rootKey: string): void {
        this.sources.globalKeys.add(rootKey);
    }

    protected addColumnInRow(rootKey: string, rowID: string, columnKey: string): void {
        this.sources.columnsInRows.get(rootKey).get(rowID).add(columnKey);
    }

    private makeSubscriptionHandler(onChange: () => void): RowSubscriptionHandler | undefined {
        if (this.sources.globalKeys.size === 0 && this.sources.columnsInRows.size === 0) return undefined;

        return new RowSubscriptionHandler(this.sources, onChange);
    }

    protected addSubsidiary(subsidiary: RowHydrationValueProvider<TContext>): void {
        this.subsidiaries.push(subsidiary);
    }

    public addSubscriptionInfo(si: SubscriptionInfo): void {
        this.additionalSubscriptionInfo.push(si);
    }

    // TODO: It's very odd that this class handles state, but then also offers
    // subscription without them.  It seems this needs to be broken down
    // further.
    //
    // `componentName` is only used for debugging.
    public subscribe(subscriptionName: string, onChange: () => void): SubscriptionInfo {
        let handlerAndPath: SubscriptionHandlerAndPath | undefined;
        if (this.context.namespace !== undefined) {
            const handler = this.makeSubscriptionHandler(onChange);
            if (handler !== undefined) {
                const path = addSubscriptionHandler(this.context, subscriptionName, handler);
                handlerAndPath = { handler, path };
            }
        }

        return {
            handlerAndPath,
            tokens: this.tokens,
            needsParsedPath: this.needsParsedPath,
            subsidiaries: [
                ...this.subsidiaries.map(s => s.subscribe(subscriptionName, onChange)),
                ...this.additionalSubscriptionInfo,
            ],
        };
    }

    public subscribeComponent(
        c: WireComponentWithFlags | undefined,
        additionalInputs: readonly unknown[],
        subscriptionName: string,
        onChange: () => void
    ): ComponentState {
        return {
            ...this.subscribe(subscriptionName, onChange),
            stateValues: this.stateManager.stateValues,
            stateSaveKey: this.stateSaveKey,
            stateChanged: false,
            isValid: c?.isValid ?? true,
            hasValue: c?.hasValue ?? false,
            subComponentStates: [],
            additionalInputs,
        };
    }
}

export class RowValueProviderMaker implements WireRowValueProviderMaker {
    constructor(
        protected readonly hydrationContext: ScreenHydrationContext,
        private readonly tableName: TableName,
        protected readonly sources: MutableTableSubscriptionSources,
        private readonly containingScreenRow: Row | undefined
    ) {}

    public makeRowValueProvider(row: Row): WireRowHydrationValueProvider {
        return new RowFromTableHydrationValueProvider(
            this.hydrationContext,
            makeInternalScreenContext(row, undefined, this.containingScreenRow),
            this.tableName,
            this.sources
        );
    }
}

export class RowActionHydrationBackend<TContext extends ActionHydrationContext>
    extends RowHydrationValueProvider<TContext>
    implements WireRowActionHydrationValueProvider
{
    public getIsUserSignedIn(): boolean {
        const email = definedMap(this.context.verifiedEmailAddressPath, p => this.getGlobalValue(undefined, p));
        return email !== undefined;
    }

    public getIsTabVisible(tabScreenName: string): boolean {
        return this.context.tabScreenVisibilityPredicates.get(tabScreenName)?.(this) === true;
    }

    public getIsModalVisible(): boolean {
        return getParsedScreens(this.context.parsedPath)[1] !== undefined;
    }

    public fetchTable(tableName: TableName): void {
        this.context.fetchTableData(tableName);
    }

    public makeHydrationBackendForRow(
        row: Row | undefined,
        _tables: InputOutputTables | undefined
    ): WireRowActionHydrationValueProvider {
        const rowContext = makeInternalScreenContext(row, undefined, this.rowContext?.containingScreenRow);
        // TODO: This should probably add more info to the `componentID` to
        // disambiguate.
        const hb = new RowActionHydrationBackend(
            this.context,
            rowContext,
            this.stateManager.oldStateValues,
            this.stateSaveKey,
            this.componentID
        );
        this.addSubsidiary(hb);
        return hb;
    }
}

export interface HydrationResult {
    // These are redundant, for convenience
    readonly componentWithFlags: WireComponentHydrationResult | undefined;
    readonly component: WireComponent | null;
    readonly hb: RowComponentHydrationBackend | undefined;
    readonly oldComponentState: ComponentState | undefined;
}

export interface HydrationHelper {
    hydrateSubComponents(
        hydrators: readonly WireComponentHydratorWithID[],
        tables: InputOutputTables,
        rowContext: InternalRowContext
    ): readonly HydrationResult[];
    subscribeSubComponents(results: readonly HydrationResult[], superComponentName: string): readonly ComponentState[];

    getEditedRow(tableName: TableName): Row | undefined;
}

// We make one of these per component we hydrate, and make it keep track of
// all the values gotten.
export class RowComponentHydrationBackend
    extends RowActionHydrationBackend<ScreenHydrationContext>
    implements WireRowComponentHydrationBackend
{
    private readonly tableBackends: TableComponentHydrationBackend[] = [];
    private subComponentResults: readonly HydrationResult[] | undefined;

    constructor(
        hydrationContext: ScreenHydrationContext,
        rowScreenContext: InternalRowContext | undefined,
        oldStateValues: StateValuesMap | undefined,
        stateSaveKey: string | undefined,
        componentID: string | undefined,
        private readonly tables: InputOutputTables | undefined,
        public readonly currentScreenTitle: string | undefined,
        private readonly hydrationHelper: HydrationHelper
    ) {
        super(hydrationContext, rowScreenContext, oldStateValues, stateSaveKey, componentID);
    }

    public get screenPosition(): PageScreenPosition {
        return this.context.position;
    }

    public registerOnValueChange(columnName: string): string | undefined {
        if (this.tables === undefined) return undefined;

        const row = this.rowContext?.outputRow ?? this.rowContext?.inputRow;
        if (row === undefined) return undefined;

        const token = makeRowID();
        const column = getTableColumn(this.tables.output, columnName);
        if (column === undefined || !isPrimitiveType(column.type)) return;
        this.context.onChangeValueRows.set(token, {
            kind: "column",
            tableName: getTableName(this.tables.output),
            columnName,
            row,
        });
        this.addToken(token);
        return token;
    }

    public makeTableTransformValueProvider(tableType: TableGlideType): WireTableTransformValueProvider {
        const tableName = getTableName(tableType);
        const sources = makeTableSubscriptionSources();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const rowBackend = this;

        class TableTransformValueProvider extends RowValueProviderMaker implements WireTableTransformValueProvider {
            // This must be called (and the row backend must be subscribed),
            // or everything depended on in `makeRowValueProvider` will not
            // actually be listened to.
            public makeHydrationBackendForTable(rows: Row[]): WireTableComponentHydrationBackend {
                const tb = new TableComponentHydrationBackend(
                    this.hydrationContext,
                    rows,
                    makeInputOutputTables(tableType),
                    rowBackend.currentScreenTitle,
                    rowBackend.stateManager,
                    `${rowBackend.componentID}-table-`,
                    this.sources
                );
                rowBackend.tableBackends.push(tb);
                return tb;
            }
        }

        return new TableTransformValueProvider(this.context, tableName, sources, rowBackend.rowContext?.inputRow);
    }

    public subscribeComponent(
        c: WireComponentWithFlags | undefined,
        additionalInputs: readonly unknown[],
        subscriptionName: string,
        onChange: () => void
    ): ComponentState {
        let info = super.subscribeComponent(c, additionalInputs, subscriptionName, onChange);
        assert(info.subComponentStates.length === 0);
        info = {
            ...info,
            subsidiaries: [
                ...info.subsidiaries,
                ...this.tableBackends.map(tb => tb.subscribe(subscriptionName, onChange)),
            ],
        };
        if (this.subComponentResults !== undefined) {
            info = {
                ...info,
                subComponentStates: this.hydrationHelper.subscribeSubComponents(
                    this.subComponentResults,
                    subscriptionName
                ),
            };
        }
        return info;
    }

    public makeHydrationBackendForRow(
        row: Row | undefined,
        tables: InputOutputTables | undefined
    ): WireRowComponentHydrationBackend {
        const rowContext = makeInternalScreenContext(row, undefined, this.rowContext?.containingScreenRow);
        const hb = new RowComponentHydrationBackend(
            this.context,
            rowContext,
            this.stateManager.oldStateValues,
            this.stateSaveKey,
            this.componentID,
            tables,
            this.currentScreenTitle,
            this.hydrationHelper
        );
        this.addSubsidiary(hb);
        return hb;
    }

    public makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined {
        if (this.rowContext?.outputRow === undefined) return undefined;
        return this.makeHydrationBackendForRow(
            this.rowContext.outputRow,
            definedMap(this.tables?.output, makeInputOutputTables)
        );
    }

    public getScreenTitle(screen: ParsedScreen): string | null | undefined {
        return this.context.getScreenTitle(screen, this);
    }

    public hydrateSubComponents(
        hydrators: readonly WireComponentHydratorWithID[],
        outputTable: TableGlideType | undefined
    ): WireHydratedSubComponents {
        assert(this.subComponentResults === undefined);

        const failureResult = {
            components: [],
            allValid: true,
            haveValues: false,
            componentsRowContext: undefined,
        };

        if (this.tables === undefined || this.rowContext === undefined) return failureResult;

        let hydrationTables: InputOutputTables;
        let hydrationRowContext: InternalRowContext;

        if (outputTable !== undefined) {
            const row = this.hydrationHelper.getEditedRow(getTableName(outputTable));
            if (row === undefined) return failureResult;

            hydrationTables = makeInputOutputTables(this.tables.input, outputTable);
            hydrationRowContext = makeInternalScreenContext(
                this.rowContext?.inputRow,
                row,
                this.rowContext?.containingScreenRow
            );
        } else {
            hydrationTables = this.tables;
            hydrationRowContext = this.rowContext;
        }

        this.subComponentResults = this.hydrationHelper.hydrateSubComponents(
            hydrators,
            hydrationTables,
            hydrationRowContext
        );

        const components: (WireComponent | null)[] = [];
        let allValid = true;
        let haveValues = false;

        for (const { component, componentWithFlags: c } of this.subComponentResults) {
            if (c !== undefined) {
                if (!c.isValid) {
                    allValid = false;
                }
                if (c.hasValue) {
                    haveValues = true;
                }
            }
            components.push(component);
        }

        return { components, allValid, haveValues, componentsRowContext: hydrationRowContext };
    }
}

// This will modify the data in `sources` during hydration.
class RowFromTableHydrationValueProvider implements WireRowHydrationValueProvider {
    constructor(
        protected readonly context: ScreenHydrationContext,
        public readonly rowContext: InternalRowContext,
        private readonly tableName: TableName,
        protected readonly sources: MutableTableSubscriptionSources
    ) {}

    public getGlobalValue(tableName: TableName | undefined, path: RootPath): GroundValue {
        this.sources.globalKeys.add(path.rest.key);
        if (tableName !== undefined) {
            this.context.fetchTableData(tableName);
        }

        return this.context.namespace?.get(path);
    }

    public getColumnInRow(tableName: TableName, rootPath: RootPath, row: Row, columnPath: RelativePath): GroundValue {
        assert(isTopLevelPath(rootPath));
        assert(isKeyPath(columnPath) && columnPath.rest === undefined);

        const isDirect = row === this.rowContext.inputRow && areTableNamesEqual(tableName, this.tableName);
        (isDirect ? this.sources.columnsInDirectRows : this.sources.columnsInIndirectRows)
            .get(rootPath.rest.key)
            .add(columnPath.key);
        this.context.fetchTableData(tableName);

        return follow(row, columnPath);
    }
}

class RowFromTableHydrationBackend
    extends RowFromTableHydrationValueProvider
    implements WireRowComponentHydrationBackend
{
    constructor(
        context: ScreenHydrationContext,
        rowScreenContext: InternalRowContext,
        private readonly tables: InputOutputTables,
        public readonly currentScreenTitle: string | undefined,
        private readonly stateManager: StateManager,
        private readonly stateNamePrefix: string,
        private readonly stateTokenPrefix: string,
        sources: MutableTableSubscriptionSources,
        private readonly tokens: Set<string>,
        private readonly registerActionWithContext: (
            run: WireActionRunner,
            rowScreenContext: InternalRowContext | undefined
        ) => string
    ) {
        super(context, rowScreenContext, getTableName(tables.input), sources);
    }

    public get screenPosition(): PageScreenPosition {
        return this.context.position;
    }

    public getParsedPath(): ParsedPath {
        return panic("We don't support this yet");
    }

    public fetchTable(tableName: TableName): void {
        this.context.fetchTableData(tableName);
    }

    public registerAction(run: WireActionRunner): string {
        return this.registerActionWithContext(run, this.rowContext);
    }

    public registerOnValueChange(columnName: string): string | undefined {
        const row = this.rowContext.inputRow;
        if (row === undefined) return undefined;

        // TODO: This is duplicated in `RowComponentHydrationBackend`
        const column = getTableColumn(this.tables.output, columnName);
        if (column === undefined || !isPrimitiveType(column.type)) return undefined;

        const token = makeRowID();
        this.context.onChangeValueRows.set(token, {
            kind: "column",
            tableName: getTableName(this.tables.output),
            columnName,
            row,
        });
        this.tokens.add(token);
        return token;
    }

    public makeTableTransformValueProvider() {
        return panic("Cannot make a table from a row in a table");
    }

    public getState<T>(
        name: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        shouldSave: boolean = true,
        convert: (v: unknown) => T
    ): WireAlwaysEditableValue<T> {
        return this.stateManager.getState(
            `${this.stateNamePrefix}${name}`,
            `${this.stateTokenPrefix}${this.rowContext.inputRow?.$rowID}-${name}`,
            validate,
            defaultValue,
            shouldSave,
            convert
        );
    }

    // TODO: These two are repeated above.  It would be nice to just have them
    // once, especially if we add more.
    public getIsUserSignedIn(): boolean {
        const email = definedMap(this.context.verifiedEmailAddressPath, p => this.getGlobalValue(undefined, p));
        return email !== undefined;
    }

    public getIsTabVisible(tabScreenName: string): boolean {
        return this.context.tabScreenVisibilityPredicates.get(tabScreenName)?.(this) === true;
    }

    public getIsModalVisible(): boolean {
        return getParsedScreens(this.context.parsedPath)[1] !== undefined;
    }

    public makeHydrationBackendForRow(
        row: Row,
        tables: InputOutputTables | undefined
    ): WireRowComponentHydrationBackend {
        // There's only one call site where can can make a
        // ##hydrationBackendWithUndefinedTables and it should never call it
        // on this class.
        assert(tables !== undefined);
        const rowContext = makeInternalScreenContext(row, undefined, this.rowContext?.containingScreenRow);
        const hb = new RowFromTableHydrationBackend(
            this.context,
            rowContext,
            tables,
            this.currentScreenTitle,
            this.stateManager,
            this.stateNamePrefix,
            this.stateTokenPrefix,
            this.sources,
            this.tokens,
            this.registerActionWithContext
        );
        return hb;
    }

    public makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined {
        return panic("We don't support this yet");
    }

    public getScreenTitle(screen: ParsedScreen): string | null | undefined {
        return this.context.getScreenTitle(screen, this);
    }

    public hydrateSubComponents(): WireHydratedSubComponents {
        return panic("We don't support this yet");
    }
}

export class TableComponentHydrationBackend implements WireTableComponentHydrationBackend {
    private readonly tokens = new Set<string>();

    constructor(
        private readonly context: ScreenHydrationContext,
        private rows: readonly Row[],
        private readonly tables: InputOutputTables,
        public readonly currentScreenTitle: string | undefined,
        private readonly stateManager: StateManager,
        private readonly stateTokenPrefix: string,
        private readonly sources: MutableTableSubscriptionSources = makeTableSubscriptionSources()
    ) {
        assert(tables.input === tables.output);
    }

    public get tableScreenContext(): readonly Row[] {
        return this.rows;
    }

    public registerAction(run: WireActionRunner, rowContext?: InternalRowContext): string {
        const token = makeRowID();
        this.context.actions.set(token, [run, rowContext ?? emptyInternalScreenContext]);
        this.tokens.add(token);
        return token;
    }

    public makeHydrationBackendForRow(row: Row): WireRowComponentHydrationBackend {
        const rowScreenContext = makeInternalScreenContext(row, undefined, undefined);

        return new RowFromTableHydrationBackend(
            this.context,
            rowScreenContext,
            this.tables,
            this.currentScreenTitle,
            this.stateManager,
            "row-",
            `${this.stateTokenPrefix}-row-`,
            this.sources,
            this.tokens,
            (run, ctx) => this.registerAction(run, ctx)
        );
    }

    private makeSubscriptionHandler(onChange: () => void): TableSubscriptionHandler | undefined {
        if (
            this.sources.globalKeys.size === 0 &&
            this.sources.columnsInDirectRows.size === 0 &&
            this.sources.columnsInIndirectRows.size === 0
        ) {
            return undefined;
        }

        return new TableSubscriptionHandler(this.sources, onChange);
    }

    public subscribe(subscriptionName: string, onChange: () => void): ComponentState {
        let handlerAndPath: SubscriptionHandlerAndPath | undefined;
        if (this.context.namespace !== undefined) {
            const handler = this.makeSubscriptionHandler(onChange);
            if (handler !== undefined) {
                const path = addSubscriptionHandler(this.context, subscriptionName, handler);
                handlerAndPath = { handler, path };
            }
        }

        return {
            handlerAndPath,
            tokens: this.tokens,
            needsParsedPath: false,
            subsidiaries: [],
            stateValues: new Map(),
            stateSaveKey: "NOT SUPPORTED",
            stateChanged: false,
            isValid: true,
            hasValue: false,
            subComponentStates: [],
            additionalInputs: [],
        };
    }

    // We could alternatively make a new instance with the new rows, but we'd have to be careful
    // to keep modifying the same state as this instance, or subscribing won't work.
    public withRows(rows: readonly Row[]): WireTableComponentHydrationBackend {
        this.rows = rows;
        return this;
    }

    public getParsedPath(): ParsedPath {
        return panic("Not implemented here");
    }
}
